/**
 * ブランドロゴ属性 (background / color_type / layout) の値→日本語ラベル変換。
 *
 * 既存のロゴ管理モーダル (LogoManagementModal / LogoEditModal) や
 * BrandDetailPage が個別に持っていたラベル定義の正本。ロゴ1枚シート
 * (renderLogoSheet) のキャプション描画でも同じラベルを使う。
 */

export interface BrandLogoLike {
  background?: string | null;
  color_type?: string | null;
  layout?: string | null;
  description?: string | null;
}

export const BACKGROUND_LABELS: Record<string, string> = {
  white_back: '白バック',
  black_back: '黒バック',
  transparent: '透過',
};

export const COLOR_TYPE_LABELS: Record<string, string> = {
  full_color: 'フルカラー',
  monochrome: 'モノクロ（黒）',
  white: '白抜き',
  solid_color: '単色（ブランドカラー）',
  grayscale: 'グレースケール',
  gradient: 'グラデーション',
  flat: 'フラット',
};

export const LAYOUT_LABELS: Record<string, string> = {
  horizontal: '横組み',
  vertical: '縦組み',
  square: 'スクエア',
  logotype: 'ロゴタイプのみ',
  symbol: 'シンボル',
  lockup: 'アイコン+ロゴ',
  app_icon: 'アプリアイコン',
  circle: 'サークル',
};

/** ロゴの属性 (背景/カラー/レイアウト) を表示用ラベル配列に変換する */
export function getLogoAttributeLabels(logo: BrandLogoLike): string[] {
  const parts: string[] = [];
  if (logo.background && BACKGROUND_LABELS[logo.background]) {
    parts.push(BACKGROUND_LABELS[logo.background]);
  }
  if (logo.color_type && COLOR_TYPE_LABELS[logo.color_type]) {
    parts.push(COLOR_TYPE_LABELS[logo.color_type]);
  }
  if (logo.layout && LAYOUT_LABELS[logo.layout]) {
    parts.push(LAYOUT_LABELS[logo.layout]);
  }
  return parts;
}

/**
 * 透過ロゴを非透過シート上に描画する際のカード地色。
 * - 透過 かつ 白抜き(color_type='white') → 黒カード (白抜きロゴが見えるように)
 * - それ以外 → 白カード
 * white_back / black_back のロゴは画像自体に地色があるため白カードで可。
 */
export function logoCardBackground(logo: BrandLogoLike): '#000000' | '#ffffff' {
  if (logo.background === 'transparent' && logo.color_type === 'white') {
    return '#000000';
  }
  return '#ffffff';
}

/** 一覧やドロップダウンに出す代表ロゴ: 既定 (is_default) → 無ければ並び順の先頭。ロゴが無ければ null */
export function pickPrimaryLogo<T extends { is_default?: boolean }>(logos?: readonly T[] | null): T | null {
  if (!logos || logos.length === 0) return null;
  return logos.find((logo) => logo.is_default) ?? logos[0];
}
